import { paymentDone } from '@svenskaspelab/nova-payment-sport';
import type { trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { DialogConfig } from '@svs/nova-foundation/nextjs/dialog';
import { createElement } from 'react';

import { getFormattedCurrency } from '../../lib/entry.browser.js';

/**
 * Middleware for handling payment completion dialogs.
 *
 * Shows an info dialog on payment success or error.
 *
 * @param showDialog - Function to display a dialog.
 * @param paymentUrls Urls for redirecting after payment
 * @param trackEventFunction Optional function to track analytics events, used for tracking when users interact with the payment done dialog.
 * @returns Middleware function for payment completion.
 */
export function paymentDoneMw(
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>,
  paymentUrls?: Record<string, string | undefined>,
  trackEventFunction?: typeof trackEvent
): ReturnType<typeof paymentDone> {
  return paymentDone(
    async (paymentSuccessContext, paymentErrorContext) => {
      if (paymentSuccessContext) {
        const { title, message, mybetsUrl, homeUrl, isExternalWager, isRetailer, onCallback } = paymentSuccessContext;
        const buttons = isRetailer
          ? {
              primary: {
                label: 'Ok',
                action: (): void => {
                  onCallback('ok');
                },
              },
            }
          : {
              secondary: {
                label: 'Mina spel',
                action: (): void => {
                  onCallback('my-games');
                  window.location.href = mybetsUrl;
                },
              },
              primary: {
                label: 'Ok',
                action: (): void => {
                  onCallback('ok');
                  if (isExternalWager) {
                    window.location.href = homeUrl;
                  }
                },
              },
            };
        await showDialog({
          title: title,
          description: createElement('p', { className: 'text-center lg:text-left' }, message),
          buttons: buttons,
        });
      } else if (paymentErrorContext) {
        const { clientErrors } = paymentErrorContext;
        await showDialog({
          title: clientErrors.title,
          description: clientErrors.message,
          buttons: {
            primary: { label: 'Ok' },
          },
        });
      }
    },
    {
      paymentUrls,
      currencyFormatter: getFormattedCurrency,
      trackEventFunction,
    }
  );
}
